import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../lib/api.js';

/**
 * Per-import configuration — the policies the worker threads into the run. Chosen
 * per import (not global); unset fields fall back to the documented defaults
 * (cascade+review matching, skip-on-rerun, link-only catalog).
 */
const OPTIONS = {
  patientMatch: [
    { v: 'cascade-review', l: 'Cascade + review (recommended)' },
    { v: 'cascade-insert', l: 'Cascade, auto-insert' },
    { v: 'source-id-only', l: 'Source-ID (PHARM1) only' },
  ],
  upsertPolicy: [
    { v: 'skip', l: 'Skip — leave existing rows' },
    { v: 'refresh-empty', l: 'Refresh empty fields only' },
    { v: 'refresh-all', l: 'Refresh all fields (overwrite)' },
  ],
  catalog: [
    { v: 'link-only', l: 'Link existing catalog only' },
    { v: 'get-or-create', l: 'Get or create catalog rows' },
  ],
  documentsTarget: [
    { v: 'pt_documents', l: 'pt_documents (reference only)' },
    { v: 'cerbo-documents-s3', l: 'Cerbo documents + S3 blob copy' },
  ],
  notesTarget: [
    { v: 'pt_notes', l: 'pt_notes (clinical note)' },
    { v: 'soap', l: 'soap (encounter note)' },
  ],
} as const;

type OptKey = keyof typeof OPTIONS;

export function ImportConfigForm({ sessionId }: { sessionId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['import-config', sessionId], queryFn: () => api.getImportConfig(sessionId) });
  const [cfg, setCfg] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!data?.importConfig) return;
    const ic = data.importConfig as Record<string, unknown>;
    const flat: Record<string, string> = {};
    for (const k of ['patientMatch', 'upsertPolicy', 'catalog']) if (typeof ic[k] === 'string') flat[k] = ic[k] as string;
    const docT = (ic.documents as { target?: string } | undefined)?.target;
    if (docT) flat.documentsTarget = docT;
    const noteT = (ic.notes as { target?: string } | undefined)?.target;
    if (noteT) flat.notesTarget = noteT;
    setCfg(flat);
  }, [data]);

  const save = useMutation({
    // Re-nest the flat form state into the ImportConfig shape (documents/notes are
    // nested). Spread the loaded config first so fields the form doesn't manage
    // (e.g. freetextFallback, pharm1Qualify, sourceSystem) are preserved, not wiped.
    mutationFn: () => {
      const out: Record<string, unknown> = { ...((data?.importConfig as Record<string, unknown>) ?? {}) };
      for (const k of ['patientMatch', 'upsertPolicy', 'catalog']) if (cfg[k]) out[k] = cfg[k];
      if (cfg.documentsTarget) out.documents = { target: cfg.documentsTarget };
      if (cfg.notesTarget) out.notes = { target: cfg.notesTarget };
      return api.setImportConfig(sessionId, out);
    },
    onSuccess: () => {
      toast.success('Import config saved');
      void qc.invalidateQueries({ queryKey: ['import-config', sessionId] });
    },
    onError: (e) => toast.error(`Save failed: ${String(e)}`),
  });

  const ctl = { background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' };
  const field = (key: OptKey, label: string) => (
    <label className="flex items-center justify-between gap-2">
      <span style={{ color: 'var(--text-dimmed)' }}>{label}</span>
      <select
        className="w-60 rounded px-1.5 py-1"
        style={ctl}
        value={cfg[key] ?? OPTIONS[key][0].v}
        onChange={(e) => setCfg((c) => ({ ...c, [key]: e.target.value }))}
      >
        {OPTIONS[key].map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-2 text-[12px]">
      {field('patientMatch', 'Patient matching')}
      {field('upsertPolicy', 'On re-run (changed data)')}
      {field('catalog', 'Medication / allergen catalog')}
      {field('documentsTarget', 'Documents target')}
      {field('notesTarget', 'Text notes target')}
      <div className="flex items-center gap-2">
        <button
          className="rounded px-3 py-1.5 font-medium"
          style={{ ...ctl, opacity: save.isPending ? 0.6 : 1 }}
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save config'}
        </button>
        <span className="text-[10px]" style={{ color: 'var(--text-dimmed)' }}>
          Applies to this import. Unset = defaults (cascade+review · skip · link-only).
        </span>
      </div>
    </div>
  );
}
